<template>
    <div v-if="canViewBillingData">
        <b-alert v-model="alert.show" :variant="alert.type" dismissible>
            {{ alert.message }}
        </b-alert>

        <colour-legend :items="colourLegend" :dark="true"></colour-legend>

        <filter-ui :fields="getFilterFields()" ref="billingManagementFilter" :apiBaseUrl="apiBaseUrl" relatedTo="billingmanagement" :filter="initialFilter" v-on:change="filterChanged"></filter-ui>
        <ejs-grid id="billingManagementGrid"
                  ref="billingManagementGrid"
                  :dataSource="gridData"
                  :allowExcelExport="true"
                  :enableStickyHeader="true"
                  :toolbar="toolbar"
                  :allowSorting="true"
                  :sortSettings="sortSettings"
                  :allowGrouping="false"
                  :groupSettings="groupSettings"
                  :allowResizing="true"
                  :allowPaging="true"
                  :pageSettings="pageSettings"
                  :showColumnChooser="true"
                  :excelQueryCellInfo="customiseCell"
                  :dataBound="gridDataBound"
                  :dataStateChange="gridDataStateChanged"
                  :queryCellInfo="customiseCell"
                  :toolbarClick="toolbarClicked"
                  :created="gridCreated"
                  :detailTemplate="detailTemplate"
                  :rowSelected="rowSelectionChanged"
                  :rowDeselected="rowSelectionChanged"
                  :rowDataBound="rowDataBound">
            <e-columns>
                <e-column field="key" :isPrimaryKey="true" :visible="false" :showInColumnChooser="false"></e-column>
                <e-column field="epiqReference" headerText="Epiq #" width="100"></e-column>
                <e-column field="orderNumber" headerText="Order Number"></e-column>
                <e-column field="orderingClient" headerText="Ordering Client"></e-column>
                <e-column field="orderingClientContact" headerText="Ordering Client Contact"></e-column>
                <e-column field="clientReference" headerText="Client Reference"></e-column>
                <e-column field="orderedDateTime" headerText="Ordered Date/Time"></e-column>
                <e-column field="serviceType" :allowSorting="false" :allowGrouping="false" headerText="Service Type"></e-column>
                <e-column field="turnaround" :allowSorting="false" :allowGrouping="false" headerText="Turnaround"></e-column>
                <e-column field="matterNumber" :headerText="$t('Vue.Grid.Shared.HeaderText.MatterNumber')"></e-column>
                <e-column field="matterName" :headerText="$t('Vue.Grid.Shared.HeaderText.MatterNameOrDescription')"></e-column>
                <e-column field="courtOrganisation" headerText="Court/Tribunal/Interview Organisation/Other "></e-column>
                <e-column field="jurisdiction" headerText="Jurisdiction"></e-column>
                <e-column field="location" headerText="Location"></e-column>
                <e-column field="eventDate" headerText="Event Date"></e-column>
                <e-column field="dueDateTimeUTC" :allowSorting="false" :allowGrouping="false" headerText="Due Date/Time"></e-column>
                <e-column field="completedDeliveredDateTime" headerText="Completed Date/Time"></e-column>
                <e-column field="totalAmount" headerText="Total Amount"></e-column>
                <e-column field="billingStatus.displayName" :allowSorting="false" :allowGrouping="false" headerText="Status"></e-column>
                <e-column field="invoiceReference" headerText="Reference"></e-column>
                <e-column field="deliveredLateReason" :allowSorting="false" :allowGrouping="false" headerText="Delivered Late Reason"></e-column>
                <e-column field="deliveredLateComments" :allowSorting="false" :allowGrouping="false" headerText="Delivered Late Comments"></e-column>
                <e-column field="credit" :allowGrouping="false" headerText="Credit Amount"></e-column>
                <e-column field="deposit" :allowSorting="false" :allowGrouping="false" headerText="Deposit Amount"></e-column>
            </e-columns>
        </ejs-grid>

    </div>
</template>

<script>
    import Vue from 'vue';
    import BootstrapVue from 'bootstrap-vue';
    import { GridPlugin, ColumnChooser, Resize, Sort, Group, Page, Toolbar, Search, ExcelExport, DetailRow, Edit } from '@syncfusion/ej2-vue-grids';
    import { DataManager } from "@syncfusion/ej2-data";
    import axios from 'axios';

    import FilterUI from './FilterUI';

    import ErrorsMixin from '../../mixins/ErrorsMixin';
    import DateTimeMixin from '../../mixins/DateTimeMixin';
    import GridMixins from '../../mixins/GridMixins';
    import UuidMixins from '../../mixins/UuidMixins';

    import BillingManagementGridDetailRow from './BillingManagementGridDetailRow.vue';

    Vue.use(BootstrapVue);
    Vue.use(GridPlugin);

    import ColourLegend from '../common/ColourLegend';
    Vue.component('colour-legend', ColourLegend);


    const EventBus = new Vue();

    //this is required to make the getGroupCaption function available globally and thus within the groupCaptionTemplate
    var billingManagementGrid_vueComponent = null;
    function billingManagementGrid_getGroupCaption(field, headerText, value) {
        return billingManagementGrid_vueComponent.getGroupCaption(field, headerText, value);
    }
    window.billingManagementGrid_getGroupCaption = billingManagementGrid_getGroupCaption;

    export default {
        name: 'BillingManagementGrid',
        mixins: [ErrorsMixin, DateTimeMixin, GridMixins, UuidMixins],
        components: {
            'filter-ui': FilterUI,
        },
        provide: {
            grid: [ColumnChooser, Resize, Sort, Group, Page, Toolbar, Search, ExcelExport, DetailRow, Edit],
            eventBus: EventBus
        },
        props: {
            apiBaseUrl: {
                type: String,
                required: true
            },
            pagesBaseUrl: {
                type: String,
                required: true
            },
            allCourtRegistryOptions: {
                type: Array,
                default: null
            },
            allInterviewOrganisationOptions: {
                type: Array,
                default: null
            },
            allBillingStatusOptions: {
                type: Array,
                default: null
            },
            canViewBillingData: {
                type: Boolean,
                default: false,
            },
            canModifyBillingData: {
                type: Boolean,
                default: false,
            },
            canMarkForInvoicing: {
                type: Boolean,
                default: false,
            },
            canMarkInvoiced: {
                type: Boolean,
                default: false,
            },

        },
        data: function () {
            return {
                detailTemplate: function () {
                    return { template: BillingManagementGridDetailRow };
                },
                colourLegend: [
                    { colour:"#c4efff", label: "Non-Chargeable (Charge will be waived on invoice)" },
                    { colour:"#ffcdc9", label: "Bill Later" },
                    { colour:"#fff2bf", label: "Marked for Invoicing" },
                    { colour:"#ceffbd", label: "Invoiced" }
                ],
                groupSettings: { showGroupedColumn: true, disablePageWiseAggregates: true, captionTemplate: "${billingManagementGrid_getGroupCaption(field, headerText, key)}" },
                pageSettings: { pageSize: 10, pageSizes: [5, 10, 25, 50] },
                columnFormats: {
                    orderedDateTime: {
                        type: 'datetime'
                    },
                    eventDate: {
                        type: 'datetime',
                        options: {
                            mode: 'excludetime',
                            outputAsUTC: true
                        }
                    },
                    startTime: {
                        type: 'datetime',
                        options: {
                            mode: 'onlytimewithseconds',
                            outputAsUTC: true
                        }
                    },
                    endTime: {
                        type: 'datetime',
                        options: {
                            mode: 'onlytimewithseconds',
                            outputAsUTC: true
                        }
                    },
                    dueDateTimeUTC: {
                        type: 'datetime',
                        options: {
                            mode: 'checktime'
                        }
                    },
                    completedDeliveredDateTime: {
                        type: 'datetime'
                    },
                    totalAmount: {
                        type: 'currency'
                    },
                },

                initialized: false,
                dataLoaded: false,
                show: true,
                quickFilters: null,
                initialFilter: null,
                sortSettings: {
                    columns: [{ field: "epiqReference", direction: "Ascending" },
                    { field: "orderNumber", direction: "Ascending" }]
                },
                gridDataState: {
                    filter: null,
                    search: null,
                    sorted: [{ name: "epiqReference", direction: "ascending" },
                    { name: "orderNumber", direction: "Ascending" }],
                    group: null,
                    skip: 0,
                    take: 10,
                    requiresCounts: true
                },
                gridData: { result: [], count: 0, portalUploadColumnRequired: false },
                grid: null,
                alert: {
                    message: "",
                    show: false,
                    type: "info",
                },
            }
        },
        computed: {

            toolbar: function () {
                var toolbarItems = this.defaultEJ2DataGridToolbarItems();
                toolbarItems =
                    toolbarItems.concat(
                        [
                            { text: "Mark for Invoicing", tooltipText: "Mark All for Invoicing", prefixIcon: "fas fa-check", id: "markforinvoicing" },
                            { text: "Bill Later", tooltipText: "Bill Later", prefixIcon: "fas fa-clock", id: "billlater" },
                            { text: "Bill Now", tooltipText: "Bill Now", prefixIcon: "fas fa-dollar-sign", id: "billnow" },
                        ]
                    );
                return toolbarItems;
            }

        },
        methods: {
            getFilterFields: function () {
                var filterFields = [
                    {
                        field: "Status",
                        label: "Status",
                        type: "multiselect",
                        options: this.allBillingStatusOptions,
                        placeholder: "Select Status"
                    },
                    {
                        field: "CourtRegistryId",
                        label: "Court/Tribunal/Other",
                        type: "multiselect",
                        options: this.allCourtRegistryOptions,
                        placeholder: "Select Court/Tribunal/Other Values"
                    },
                    {
                        field: "InterviewOrganisationId",
                        label: "Interview Organisation",
                        type: "multiselect",
                        options: this.allInterviewOrganisationOptions,
                        placeholder: "Select Interview Organisations"
                    },
                    {
                        field: "CompletedDate",
                        label: "Date Delivered/Completed",
                        type: "date",
                        format: "dd/MM/yyyy"
                    }];

                return filterFields;
            },
            getGroupCaption: function (field, headerText, value) {
                if (field == "sittingDate") value = this.dateTimeDisplayString(value, 'excludetime', true, true);
                else if (field == "orderedDate") value = this.dateTimeDisplayString(value, 'excludetime', true, true);
                else if (field == "interviewDateTime") value = this.dateTimeDisplayString(value, 'checktimeincludeseconds', true, true);
                else if (field == "createdDateTimeUTC") value = this.dateTimeDisplayString(value, 'includetime', true, false);
                else if (field == "orderedDateTimeUTC") value = this.dateTimeDisplayString(value, 'includetime', true, false);
                else if (field == "confirmedDateTimeUTC") value = this.dateTimeDisplayString(value, 'includetime', true, false);
                else if (field == "dueDateTimeUTC") value = this.dateTimeDisplayString(value, 'checktime', true, false);
                else if (field == "mergedDateTimeUTC") value = this.dateTimeDisplayString(value, 'includetime', true, false);
                else if (field == "checkedDateTimeUTC") value = this.dateTimeDisplayString(value, 'includetime', true, false);
                else if (field == "completedDateTimeUTC") value = this.dateTimeDisplayString(value, 'includetime', true, false);
                else if (field == "sittingStartTime") value = this.formatDateTime(value, 'hh:mm:ssA', true, true);
                else if (field == "sittingEndTime") value = this.formatDateTime(value, 'hh:mm:ssA', true, true);
                else if (field == "startTime") value = this.formatDateTime(value, 'hh:mm:ssA', true, true);
                else if (field == "endTime") value = this.formatDateTime(value, 'hh:mm:ssA', true, true);
                return headerText + ": " + value;
            },
            customizeGroupKeys(groupedDate) {
                groupedDate.forEach(g => {
                    if (g.field && g.key && g.items) {
                        if (g.field == "sittingDate") g.key = this.dateTimeDisplayString(g.key, 'excludetime', true, true);
                        else if (g.field == "orderedDate") g.key = this.dateTimeDisplayString(g.key, 'excludetime', true, true);
                        else if (g.field == "interviewDateTime") g.key = this.dateTimeDisplayString(g.key, 'checktimeincludeseconds', true, true);
                        else if (g.field == "createdDateTimeUTC") g.key = this.dateTimeDisplayString(g.key, 'includetime', true, false);
                        else if (g.field == "orderedDateTimeUTC") g.key = this.dateTimeDisplayString(g.key, 'includetime', true, false);
                        else if (g.field == "confirmedDateTimeUTC") g.key = this.dateTimeDisplayString(g.key, 'includetime', true, false);
                        else if (g.field == "dueDateTimeUTC") g.key = this.dateTimeDisplayString(g.key, 'checktime', true, false);
                        else if (g.field == "mergedDateTimeUTC") g.key = this.dateTimeDisplayString(g.key, 'includetime', true, false);
                        else if (g.field == "checkedDateTimeUTC") g.key = this.dateTimeDisplayString(g.key, 'includetime', true, false);
                        else if (g.field == "completedDateTimeUTC") g.key = this.dateTimeDisplayString(g.key, 'includetime', true, false);
                        else if (g.field == "sittingStartTime") g.key = this.formatDateTime(g.key, 'hh:mm:ssA', true, true);
                        else if (g.field == "sittingEndTime") g.key = this.formatDateTime(g.key, 'hh:mm:ssA', true, true);
                        else if (g.field == "startTime") g.key = this.formatDateTime(g.key, 'hh:mm:ssA', true, true);
                        else if (g.field == "endTime") g.key = this.formatDateTime(g.key, 'hh:mm:ssA', true, true);
                        this.customizeGroupKeys(g.items);
                    }
                });
            },
            customiseCell: function (args) {
                this.defaultEJ2DataGridQueryCellInfo(args, this.columnFormats);
            },



            gridDataBound: function () {
                this.grid.autoFitColumns();
            },

            rowDataBound: function (args) {
                //if (this.data.selectedEntry != undefined &&
                //    this.data.selectedEntry != null &&
                //    args.data.workLogEntryId == this.data.selectedEntry)
                args.row.classList.add('billing-management');
                
                if (args.data.invoiced)
                    args.row.classList.add('invoiced');

                else if (args.data.markedForInvoicing)
                    args.row.classList.add('marked-for-invoicing');

                else if (args.data.billLater)
                    args.row.classList.add('bill-later');
                

                return;
            },

            filterChanged: function (filter) {
                this.gridDataState.filter = filter;
                if (this.initialized) this.grid.refresh();

            },
            gridDataStateChanged: function (args) {
                this.gridDataState.search = args.search;
                this.gridDataState.sorted = args.sorted;
                this.gridDataState.group = args.group;
                this.gridDataState.skip = args.skip;
                this.gridDataState.take = args.take;
                this.gridDataState.requiresCounts = args.requiresCounts;
                if (this.initialized) {
                    this.grid.showSpinner();
                    this.loadInvoices(this.gridDataState)
                        .then(result => {
                            if (result) {
                                this.gridData = result;
                                //this.portalUploadFilterRequired = relatedTo.gridData.portalUploadColumnRequired;
                            }
                            this.grid.hideSpinner();
                        });
                }
            },
            rowSelectionChanged: function (args) {
                if (args.name == 'rowSelected') {
                    this.updateToolbarItems(this.grid.getSelectedRecords()[0])
                } else {
                    this.updateToolbarItems()
                }
            },
            updateToolbarItems: function (invoice = null) {
                this.setToolbarItemState(this.grid, 'markforinvoicing', this.canMarkForInvoicing);

                if (invoice != null && this.canModifyBillingData && !invoice.markedForInvoicing) {
                    this.setToolbarItemState(this.grid, 'billnow', invoice.billLater);
                    this.setToolbarItemState(this.grid, 'billlater', !invoice.billLater);
                }
                else {
                    this.setToolbarItemState(this.grid, 'billnow', false);
                    this.setToolbarItemState(this.grid, 'billlater', false);
                }
                
            },
            setToolbarItemState: function (grid, itemClass, enabled) {
                grid.toolbarModule.enableItems([itemClass], enabled);
                grid.toolbarModule.toolbar.items.find(e => e.properties.id == itemClass).visible = enabled;
            },
            toolbarClicked: function (args) {
                var selectedRow = this.grid.getSelectedRecords()[0];
                switch (args.item.id) {
                    case 'excelexport':
                        var gridDataState = {
                            ...this.gridDataState
                        };
                        //but reset the skip and take to 0 which will return everything and unset the requiresCounts cause we are not interseted in the totals
                        gridDataState.requiresCounts = false;
                        this.grid.showSpinner();
                        this.loadInvoices(gridDataState)
                            .then(result => {
                                //the result is either false if something failed during the axios request, or it will contain the list in the result property which is what we have to pass to the actual export
                                if (result) {
                                    var query = this.grid.getDataModule().generateQuery(true);
                                    var groupedData = new DataManager(result.result).executeLocal(query);
                                    this.customizeGroupKeys(groupedData);
                                    this.grid.excelExport({ dataSource: groupedData });
                                }
                                this.grid.hideSpinner();
                            });
                        break;

                    case 'markforinvoicing':
                        if (!this.canModifyBillingData) return;
                        this.$bvModal.msgBoxConfirm(
                            [
                                this.$createElement(
                                    'div',
                                    {
                                        domProps:
                                        {
                                            innerHTML: "<p>Are you sure you want to mark all worksheets matching the current filter for invoicing?</p>"
                                        }
                                    }
                                )
                            ],
                            {
                                title: 'Mark For Invoicing',
                                size: 'md',
                                okVariant: 'danger',
                                okTitle: 'Yes, Mark For Invoicing',
                                cancelTitle: 'No, Keep as Not Marked for Invoicing',
                                centered: true
                            })
                            .then(value => {
                                if (value) {
                                    this.markForInvoicing();
                                }
                            });
                        break;

                    case 'billlater':
                        if (!this.canModifyBillingData) return;
                        selectedRow.billLater = true;
                        this.updateInvoice(selectedRow);
                        break;
                    case 'billnow':
                        if (!this.canModifyBillingData) return;
                        selectedRow.billLater = false;
                        this.updateInvoice(selectedRow);
                        break;
                    default:
                        this.defaultEJ2DataGridToolbarClickHandler(args, this.grid);
                        break;

                }



            },

            loadInvoices: function (gridDataState) {
                if (!this.canViewBillingData) return;

                if (gridDataState.filter == null) {
                    this.grid.localeObj.localeStrings.EmptyRecord = "Apply a filter to see results";
                    return Promise.resolve({ count: 0, result: [] });
                }
                this.grid.localeObj.localeStrings.EmptyRecord = "No records to display";

                var url = this.apiBaseUrl + '/billingmanagement/list';
                return axios
                    .post(url, JSON.stringify(gridDataState), {
                        headers: {
                            'Content-Type': 'application/json'
                        }
                    })
                    .then(response => {
                        var gridData = response.data;


                        if (gridData && gridData.result) {
                            gridData.result = gridData.result.map((workItem) => {
                                ////IMPORTANT! ALL date/time properties have to be converted from strings to 'proper' date/time value here
                                //if (workItem.createdDateTimeUTC) workItem.createdDateTimeUTC = this.parseDateTime(workItem.createdDateTimeUTC, true, false);
                                //if (workItem.confirmedDateTimeUTC) workItem.confirmedDateTimeUTC = this.parseDateTime(workItem.confirmedDateTimeUTC, true, false);
                                //if (workItem.orderedDateTimeUTC) workItem.orderedDateTimeUTC = this.parseDateTime(workItem.orderedDateTimeUTC, true, false);
                                //if (workItem.dueDateTimeUTC) workItem.dueDateTimeUTC = this.parseDateTime(workItem.dueDateTimeUTC, true, false);
                                //if (workItem.mergedDateTimeUTC) workItem.mergedDateTimeUTC = this.parseDateTime(workItem.mergedDateTimeUTC, true, false);
                                //if (workItem.checkedDateTimeUTC) workItem.checkedDateTimeUTC = this.parseDateTime(workItem.checkedDateTimeUTC, true, false);
                                //if (workItem.completedDateTimeUTC) workItem.completedDateTimeUTC = this.parseDateTime(workItem.completedDateTimeUTC, true, false);
                                //if (workItem.sittingDate) workItem.sittingDate = this.parseDateTime(workItem.sittingDate, true, true);
                                //if (workItem.orderedDate) workItem.orderedDate = this.parseDateTime(workItem.orderedDate, true, false);
                                //if (workItem.sittingStartTime) workItem.sittingStartTime = this.parseDateTime(workItem.sittingStartTime, true, true);
                                //if (workItem.sittingEndTime) workItem.sittingEndTime = this.parseDateTime(workItem.sittingEndTime, true, true);
                                //if (workItem.interviewDateTime) workItem.interviewDateTime = this.parseDateTime(workItem.interviewDateTime, true, true);
                                //if (workItem.startTime) workItem.startTime = this.parseDateTime(workItem.startTime, true, true);
                                //if (workItem.endTime) workItem.endTime = this.parseDateTime(workItem.endTime, true, true);
                                workItem.key = this.newUuid();
                                workItem.canModifyBillingData = this.canModifyBillingData;
                                return workItem;
                            });
                        }
                        return gridData;
                    })
                    .catch(ex => {
                        var errors = this.handleAxiosException(ex);
                        this.showErrorsToast(errors);
                        return false;
                    });
            },
            updateInvoice: function (invoice) {
                if (!this.canModifyBillingData) return;

                var url = this.$urlHelper.getApiUrl('billingmanagement', 'updateinvoice');
                return axios
                    .post(url, JSON.stringify(invoice), {
                        headers: {
                            'Content-Type': 'application/json'
                        }
                    })
                    .then(() => {
                        this.grid.refresh();
                    })
                    .catch(ex => {
                        var errors = this.handleAxiosException(ex);
                        this.showErrorsToast(errors);
                        return false;
                    });
            },
            markForInvoicing: function () {
                if (!this.canMarkForInvoicing) return;
                var url = this.$urlHelper.getApiUrl('billingmanagement', 'markforinvoicing');
                this.grid.showSpinner();
                return axios
                    .post(url, JSON.stringify(this.gridDataState), {
                        headers: {
                            'Content-Type': 'application/json'
                        }
                    })
                    .then((response) => {
                        if (response.data.count > 0) {
                            this.alert.message = response.data.count + " entries have been marked for invoicing. Reference: " + response.data.reference;
                        } else {
                            this.alert.message = "Your current filter does not include any entries that can be marked for invoicing.";
                        }
                        this.alert.show = true;
                        this.grid.hideSpinner();
                        this.grid.refresh();
                    })
                    .catch(ex => {
                        var errors = this.handleAxiosException(ex);
                        this.showErrorsToast(errors);
                        return false;
                    });
            },

            gridCreated: function () {
                Vue.nextTick(() => {
                    this.defaultEJ2DataGridCreatedHandler(this.grid);
                });
            },


        },
        created: function () {
            EventBus.$on("updatetotal", (args) => {
                var row = this.gridData.result.find(e => e.key == args.key);
                row.totalAmount = args.total;
                this.grid.setCellValue(args.key, 'totalAmount', args.total);
                this.$bvToast.toast('Successfully updated Line Item.', {
                    title: 'Updated',
                    variant: 'success',
                    appendToast: true
                });
            });
        },
        mounted: function () {
            this.grid = this.$refs.billingManagementGrid.ej2Instances;
            this.setupGridPersistence(this.grid);
            this.updateToolbarItems();
            this.$refs.billingManagementFilter.loadQuickFilters()
                .then(() => {
                    this.initialized = true;
                    this.grid.refresh();
                });
        }
    }
</script>


<style>

    .billing-management {
        background-color: #fff !important;
    }

        .billing-management.non-chargeable {
            background-color: #c4efff !important;
        }

        .billing-management.bill-later {
            background-color: #ffcdc9 !important;
        }

        .billing-management.marked-for-invoicing {
            background-color: #fff2bf !important;
        }

        .billing-management.invoiced {
            background-color: #ceffbd !important;
        }
</style>